import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ProveedorSesion, useSesion } from './sesion/sesion';
import type { Sesion } from './sesion/sesion';
import { puede } from './sesion/permisos';
import type { Permiso } from './sesion/permisos';
import { Marco } from './componentes/Marco';
import { Cargando, ConError } from './componentes/Estados';
import { Ingreso, NoEncontrado, SinPermiso, SinRol } from './paginas/Acceso';
import { Panel } from './paginas/Panel';
import { Incidentes } from './paginas/Incidentes';
import { Verificacion } from './paginas/Verificacion';
import { Conductores } from './paginas/Conductores';
import { Viajes } from './paginas/Viajes';
import { Suscripciones } from './paginas/Suscripciones';
import { Dispatch } from './paginas/Dispatch';
import { Tarifario } from './paginas/Tarifario';
import { Precio } from './paginas/Precio';
import { PlanSuscripcion } from './paginas/PlanSuscripcion';
import { Auditoria } from './paginas/Auditoria';

/**
 * El mapa se carga aparte porque MapLibre pesa ~1 MB y sólo lo usa esta
 * pantalla. Sin esto, el operador que abre el panel para mirar la cola de
 * verificación se descarga igual toda la librería de mapas.
 */
const MapaOperacion = lazy(() =>
  import('./paginas/MapaOperacion').then((modulo) => ({ default: modulo.MapaOperacion })),
);

/**
 * Puerta por permiso.
 *
 * ⚠️ Esto ordena la interfaz; NO es el control de acceso. Un operador que
 * escriba `/dispatch` en la barra ve la pantalla de «sin permiso», pero eso lo
 * decide el navegador. Quien tiene que rechazar la escritura es `apps/api`, en
 * cada endpoint. Si el backend confía en que la consola ya filtró, no hay
 * control: hay una interfaz que esconde botones.
 */
function Protegida({
  sesion,
  permiso,
  children,
}: {
  sesion: Sesion;
  permiso: Permiso;
  children: ReactNode;
}) {
  return puede(sesion.rol, permiso) ? <>{children}</> : <SinPermiso />;
}

function Consola() {
  const { estado, reintentar } = useSesion();

  if (estado.estado === 'cargando') {
    return (
      <div className="ingreso">
        <div style={{ width: 'min(340px, 100%)' }}>
          <p className="rotulo" style={{ marginBottom: 10 }}>
            Consola de operación
          </p>
          <Cargando filas={3} etiqueta="Verificando la sesión" />
        </div>
      </div>
    );
  }

  if (estado.estado === 'error') {
    return (
      <div className="ingreso">
        <div className="ingreso__caja">
          <ConError
            titulo="No se pudo verificar la sesión"
            detalle={`${estado.mensaje} Si el problema sigue, avisá a quien opere el backend antes de seguir intentando.`}
            alReintentar={reintentar}
          />
        </div>
      </div>
    );
  }

  if (estado.estado === 'sin-sesion') return <Ingreso />;
  if (estado.estado === 'sin-rol') return <SinRol correo={estado.correo} />;

  const { sesion } = estado;

  return (
    <Routes>
      <Route element={<Marco sesion={sesion} />}>
        <Route index element={<Panel />} />
        <Route
          path="mapa"
          element={
            <Suspense fallback={<Cargando filas={3} etiqueta="Cargando el mapa" />}>
              <MapaOperacion />
            </Suspense>
          }
        />
        <Route path="incidentes" element={<Incidentes />} />
        <Route path="verificacion" element={<Verificacion />} />
        <Route path="conductores" element={<Conductores />} />
        <Route path="viajes" element={<Viajes />} />
        <Route path="suscripciones" element={<Suscripciones />} />

        <Route
          path="dispatch"
          element={
            <Protegida sesion={sesion} permiso="editar.dispatch">
              <Dispatch />
            </Protegida>
          }
        />
        <Route
          path="tarifario"
          element={
            <Protegida sesion={sesion} permiso="editar.tarifario">
              <Tarifario />
            </Protegida>
          }
        />
        <Route
          path="precio"
          element={
            <Protegida sesion={sesion} permiso="editar.precio">
              <Precio />
            </Protegida>
          }
        />
        <Route
          path="plan-suscripcion"
          element={
            <Protegida sesion={sesion} permiso="editar.plan-suscripcion">
              <PlanSuscripcion />
            </Protegida>
          }
        />
        <Route
          path="auditoria"
          element={
            <Protegida sesion={sesion} permiso="ver.auditoria">
              <Auditoria />
            </Protegida>
          }
        />

        <Route path="ingreso" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NoEncontrado />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ProveedorSesion>
        <Consola />
      </ProveedorSesion>
    </BrowserRouter>
  );
}
